/**
 * FormView.tsx - FormView - Dynamic form builder for data entry
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useDatasetView } from '../base/useDatasetView';
import { useViewMetadata } from '../base/useViewMetadata';
import { useBacktrackr } from '@opcentral/backtrackr';
import FormField from './FormField';
import './FormView.css';

const FormView = ({
  viewId = 'form-default',
  datasetContext,
  dataContext,
  backtrackrContext: propBacktrackrContext,
  config = {}
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const {
    items,
    fieldMappings,
    getField,
    updateItem,
    addItem,
    deleteItem
  } = useDatasetView('form', datasetContext, dataContext);
  
  const hookBacktrackrContext = useBacktrackr();
  const backtrackrContext = propBacktrackrContext || hookBacktrackrContext;
  
  const {
    getMetadata,
    setMetadata
  } = useViewMetadata(viewId, items, updateItem);
  
  // Extract config
  const formConfig = config.form || {};
  const fields = formConfig.fields || [];
  const steps = formConfig.steps || [{ title: 'Form', fields }];
  const showProgress = formConfig.showProgress !== false;
  const allowDrafts = formConfig.allowDrafts !== false;
  
  // Get current step fields
  const currentStepFields = useMemo(() => {
    return steps[currentStep]?.fields || [];
  }, [steps, currentStep]);
  
  // Validate field
  const validateField = useCallback((field, value) => {
    const errors = [];
    
    if (field.required && (!value || value === '')) {
      errors.push(`${field.label} is required`);
    }
    
    if (field.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        errors.push('Invalid email address');
      }
    }
    
    if (field.type === 'url' && value) {
      try {
        new URL(value);
      } catch {
        errors.push('Invalid URL');
      }
    }
    
    if (field.minLength && value && value.length < field.minLength) {
      errors.push(`Minimum ${field.minLength} characters required`);
    }
    
    if (field.maxLength && value && value.length > field.maxLength) {
      errors.push(`Maximum ${field.maxLength} characters allowed`);
    }
    
    if (field.pattern && value) {
      const regex = new RegExp(field.pattern);
      if (!regex.test(value)) {
        errors.push(field.patternMessage || 'Invalid format');
      }
    }
    
    return errors;
  }, []);
  
  // Handle field change
  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    
    // Clear errors for this field
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);
  
  // Validate current step
  const validateStep = useCallback(() => {
    const stepErrors = {};
    
    currentStepFields.forEach(field => {
      const fieldErrors = validateField(field, formData[field.name]);
      if (fieldErrors.length > 0) {
        stepErrors[field.name] = fieldErrors;
      }
    });
    
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  }, [currentStepFields, formData, validateField]);
  
  // Handle next step
  const handleNext = useCallback(() => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  }, [validateStep, steps.length]);
  
  // Handle previous step
  const handlePrevious = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, []);
  
  // Handle submit
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateStep()) return;
    
    setIsSubmitting(true);
    
    try {
      // Convert form data to item format
      const itemData = {};
      Object.entries(formData).forEach(([key, value]) => {
        const mappedField = fieldMappings[key];
        if (mappedField) {
          itemData[mappedField] = value;
        }
      });
      
      await addItem(itemData);
      
      // Reset form
      setFormData({});
      setErrors({});
      setCurrentStep(0);
      
      alert('Form submitted successfully!');
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Error submitting form');
    } finally {
      setIsSubmitting(false);
    }
  }, [validateStep, formData, fieldMappings, addItem]);
  
  // Handle save draft
  const handleSaveDraft = useCallback(() => {
    const draftData = {
      ...formData,
      _isDraft: true,
      _savedAt: new Date().toISOString()
    };
    
    localStorage.setItem(`form-draft-${viewId}`, JSON.stringify(draftData));
    alert('Draft saved!');
  }, [formData, viewId]);
  
  // Load draft on mount
  React.useEffect(() => {
    if (allowDrafts) {
      const savedDraft = localStorage.getItem(`form-draft-${viewId}`);
      if (savedDraft) {
        try {
          const draftData = JSON.parse(savedDraft);
          if (window.confirm('Load saved draft?')) {
            setFormData(draftData);
          }
        } catch (error) {
          console.error('Error loading draft:', error);
        }
      }
    }
  }, [viewId, allowDrafts]);
  
  const progress = ((currentStep + 1) / steps.length) * 100;
  
  return (
    <div className="form-view">
      <div className="form-container">
        <div className="form-header">
          <h2>{steps[currentStep]?.title || 'Form'}</h2>
          
          {showProgress && steps.length > 1 && (
            <div className="form-progress">
              <div className="form-progress-bar" style={{ width: `${progress}%` }} />
              <div className="form-progress-text">
                Step {currentStep + 1} of {steps.length}
              </div>
            </div>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="form-body">
          {currentStepFields.map(field => (
            <FormField
              key={field.name}
              field={field}
              value={formData[field.name]}
              onChange={(value) => handleFieldChange(field.name, value)}
              errors={errors[field.name]}
            />
          ))}
          
          <div className="form-actions">
            {currentStep > 0 && (
              <button type="button" onClick={handlePrevious} className="btn-form-previous">
                Previous
              </button>
            )}
            
            {allowDrafts && (
              <button type="button" onClick={handleSaveDraft} className="btn-form-draft">
                Save Draft
              </button>
            )}
            
            {currentStep < steps.length - 1 ? (
              <button type="button" onClick={handleNext} className="btn-form-next">
                Next
              </button>
            ) : (
              <button type="submit" disabled={isSubmitting} className="btn-form-submit">
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormView;
